import React, { useCallback, useEffect, useRef } from "react";
import { Minimize2 } from "lucide-react";
import { TASK_LABEL } from "./taskCopy";
import "./TaskFocusWorkspace.css";

/**
 * Full-viewport chrome around the existing Task editor.
 * Children stay mounted when opening or closing so guided-flow state is shared.
 */
export function TaskExpandButton({
  onClick,
  buttonRef = null,
  testId = "task-expand-button",
  disabled = false,
  label = "Expand",
}) {
  const accessible =
    label === "Open Task" ? `Open ${TASK_LABEL}` : `Expand ${TASK_LABEL} into focus mode`;
  return (
    <button
      type="button"
      className="task-expand-button"
      data-testid={testId}
      aria-label={accessible}
      onClick={onClick}
      ref={buttonRef}
      disabled={disabled}
    >
      <span>{label}</span>
    </button>
  );
}

function TaskFocusErrorBoundary({ onError, fallback, children }) {
  return (
    <TaskFocusErrorBoundaryInner onError={onError} fallback={fallback}>
      {children}
    </TaskFocusErrorBoundaryInner>
  );
}

class TaskFocusErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    if (typeof this.props.onError === "function") this.props.onError();
  }

  render() {
    if (this.state.hasError) return this.props.fallback || null;
    return this.props.children;
  }
}

function getFocusable(root) {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true");
}

export default function TaskFocusWorkspace({
  open,
  title,
  repoLabel,
  statusLabel,
  saveState = "Saved",
  onExit,
  escapeBlocked = false,
  hasUnsavedLocalInput = false,
  children,
}) {
  const rootRef = useRef(null);
  const headingRef = useRef(null);
  const bodyRef = useRef(null);
  const scrollRef = useRef(0);
  const openedRef = useRef(false);
  const [exitConfirm, setExitConfirm] = React.useState(false);

  const requestExit = useCallback(() => {
    if (escapeBlocked) return;
    if (hasUnsavedLocalInput) {
      setExitConfirm(true);
      return;
    }
    setExitConfirm(false);
    if (typeof onExit === "function") onExit();
  }, [escapeBlocked, hasUnsavedLocalInput, onExit]);

  useEffect(() => {
    if (!open) {
      setExitConfirm(false);
      openedRef.current = false;
      return undefined;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (bodyRef.current) bodyRef.current.scrollTop = scrollRef.current;
    if (!openedRef.current) {
      headingRef.current?.focus?.();
      openedRef.current = true;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (exitConfirm) {
          event.preventDefault();
          setExitConfirm(false);
          return;
        }
        if (escapeBlocked || hasUnsavedLocalInput) {
          event.preventDefault();
          if (hasUnsavedLocalInput && !escapeBlocked) setExitConfirm(true);
          return;
        }
        event.preventDefault();
        requestExit();
        return;
      }
      if (event.key !== "Tab" || !rootRef.current) return;
      const focusable = getFocusable(rootRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, escapeBlocked, hasUnsavedLocalInput, exitConfirm, requestExit]);

  const onBodyScroll = (event) => {
    scrollRef.current = event.currentTarget.scrollTop;
  };

  return (
    <TaskFocusErrorBoundary
      onError={() => {
        if (typeof onExit === "function") onExit();
      }}
      fallback={children}
    >
      <div
        className={open ? "task-focus-workspace is-open" : "task-focus-workspace"}
        data-testid="task-focus-workspace"
        data-open={open ? "true" : "false"}
        ref={rootRef}
        {...(open
          ? {
              role: "dialog",
              "aria-modal": "true",
              "aria-labelledby": "task-focus-title",
            }
          : {})}
      >
        <header className="task-focus-header" hidden={!open}>
          <div className="task-focus-header-main">
            <p className="task-focus-kicker">{TASK_LABEL}</p>
            <h2
              id="task-focus-title"
              className="task-focus-title"
              tabIndex={-1}
              ref={headingRef}
              data-testid="task-focus-title"
            >
              {title || `Untitled ${TASK_LABEL}`}
            </h2>
            <p className="task-focus-repo" data-testid="task-focus-repo">
              {repoLabel}
            </p>
          </div>
          <div className="task-focus-header-meta">
            {statusLabel ? (
              <span className="task-focus-status" data-testid="task-focus-status">
                {statusLabel}
              </span>
            ) : null}
            <span className="task-focus-save" data-testid="task-focus-save-state">
              {saveState}
            </span>
            <button
              type="button"
              className="task-focus-exit"
              data-testid="task-focus-exit"
              onClick={requestExit}
            >
              <Minimize2 size={16} aria-hidden="true" />
              Exit focus mode
            </button>
          </div>
          {exitConfirm ? (
            <div
              className="task-focus-exit-confirm"
              role="alertdialog"
              aria-labelledby="task-focus-exit-confirm-title"
              data-testid="task-focus-exit-confirm"
            >
              <p id="task-focus-exit-confirm-title">
                Unsaved answers or notes on this screen will be lost if you exit.
              </p>
              <button
                type="button"
                className="task-focus-exit"
                data-testid="task-focus-exit-confirm-leave"
                onClick={() => {
                  setExitConfirm(false);
                  if (typeof onExit === "function") onExit();
                }}
              >
                Exit anyway
              </button>
              <button
                type="button"
                className="task-expand-button"
                data-testid="task-focus-exit-confirm-stay"
                onClick={() => setExitConfirm(false)}
              >
                Keep editing
              </button>
            </div>
          ) : null}
        </header>
        <div
          className="task-focus-body"
          data-testid="task-focus-body"
          ref={bodyRef}
          onScroll={onBodyScroll}
        >
          {children}
        </div>
      </div>
    </TaskFocusErrorBoundary>
  );
}
